import { defineStore } from 'pinia';

import Formatter from '@/Utils/Formatter';
import { currencyChoiceType } from '@/Types/CurrencyChoiceTypes.js';
import { dccType } from '@/Types/DccTypes.js';
import { fetchAmount, fetchCurrencies, fetchDepositAmount, fetchInstallments, fetchMethods, fetchPaymentTypes } from '@/Services/Payments/PaymentContextRequests';
import { useCurrencyStore } from '@/Stores/Web/currencyStore';
import { useOrderStore } from '@/Stores/Web/orderStore';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';

// Guards the whole refresh, not a single request - an older one must not write over what a newer one settled on.
let latestRefreshId = 0;

// Set while a checkout is initialising, so a command fired meanwhile can wait for it instead of racing it.
let initialization = null;

const notChosen = { type: currencyChoiceType.notChosen, shortName: null };
const projectCurrencyChosen = { type: currencyChoiceType.projectCurrency, shortName: null };

// What the backer picked in this checkout.
let currencyChoice = notChosen;

// A null list means the currencies were not reloaded - the one the user just picked stands.
function settleCurrency({ currencies, selectedCurrency, suggestedCurrency }) {
    if (!currencies) {
        return { currency: selectedCurrency };
    }

    const serverSuggested = currencies.find(({ isSuggested }) => isSuggested)?.shortName ?? suggestedCurrency;
    const systemSelected = currencies.find(({ isSelected }) => isSelected);

    // A reloaded list brings fresh rates, not a fresh decision - the pick stands while it is still offered.
    const isOtherCurrencyChosen = currencyChoice.type === currencyChoiceType.otherCurrency;
    const offeredPick = isOtherCurrencyChosen
        ? currencies.find(({ shortName }) => shortName === currencyChoice.shortName)
        : undefined;
    const currency = currencyChoice.type !== currencyChoiceType.notChosen
        ? offeredPick ?? null
        : systemSelected && serverSuggested
            ? currencies.find(({ shortName }) => shortName === serverSuggested) ?? null
            : null;

    // The pick is no longer offered, so it becomes the project currency for good - remembering it would
    // bring it back on its own once the backer returns to a location that offers it.
    if (isOtherCurrencyChosen && !offeredPick) {
        currencyChoice = projectCurrencyChosen;
    }

    return {
        currencies,
        // The dropdown has to name the currency the amounts are in - the server suggestion would contradict them.
        suggested: offeredPick?.shortName ?? serverSuggested,
        option: currency ? dccType.systemSuggestion : dccType.projectCurrency,
        currency,
    };
}

// Owns what the server says about paying for the order - every input goes through one command, one refresh.
export const usePaymentContextStore = defineStore('paymentContext', {
    state: () => ({
        isInitialized: false,
        checkoutMode: null,
        installments: [],
        installmentValue: null,
        installmentsCurrencySymbol: null,
        actionType: null,
        newPlanAmountConverted: null,
        previousPlanAmountConverted: null,
        upgradeAmountConverted: null,
    }),
    actions: {
        async initialize({ pricedOrderCode = null, installmentsOrderCode = pricedOrderCode, paymentType, checkoutMode = null }) {
            const orderStore = useOrderStore();

            // The store outlives a single checkout, so a new one must not inherit the previous state.
            this.isInitialized = false;
            this.checkoutMode = checkoutMode;
            currencyChoice = notChosen;

            orderStore.setPricedOrders({ pricedOrderCode, installmentsOrderCode });
            orderStore.setOrderPaymentType(paymentType);

            // The flag is set inside the promise, so anyone awaiting it already sees a settled state.
            const attempt = (async () => {
                await this.refresh();
                this.isInitialized = true;
            })();

            // Commands await the swallowed copy - a failed attempt must not keep re-throwing at them; they
            // read isInitialized instead and give up quietly. The caller below still gets the error.
            initialization = attempt.catch(() => {});

            await attempt;
        },
        // Commands can arrive before the checkout finishes initialising - they wait rather than race it.
        async ensureInitialized() {
            await initialization;

            return this.isInitialized;
        },
        async setLocation() {
            if (!await this.ensureInitialized()) {
                return;
            }

            // A new location brings its own suggested currency - the pick made for the previous one does not follow.
            currencyChoice = notChosen;

            await this.refresh();
        },
        async setShippingModel() {
            if (!await this.ensureInitialized()) {
                return;
            }

            await this.refresh();
        },
        async setGiftCards() {
            if (!await this.ensureInitialized()) {
                return;
            }

            await this.refresh();
        },
        async setPaymentType(paymentType) {
            if (!await this.ensureInitialized()) {
                return;
            }

            useOrderStore().setOrderPaymentType(paymentType);

            await this.refresh();
        },
        async setCurrency(currency) {
            if (!await this.ensureInitialized()) {
                return;
            }

            currencyChoice = currency
                ? { type: currencyChoiceType.otherCurrency, shortName: currency.shortName }
                : projectCurrencyChosen;
            useCurrencyStore().setSelectedCurrency(currency);

            // The list stays as it is - reloading it would replace the currency the user has just picked.
            await this.refresh({ reloadCurrencies: false });
        },
        // The currency is settled first and handed on explicitly; nothing is written until every answer is in.
        async refresh({ reloadCurrencies = true } = {}) {
            const refreshId = ++latestRefreshId;
            const currencyStore = useCurrencyStore();
            const orderStore = useOrderStore();
            const { isDccEnabled, projectID } = useProjectContextStore();
            const isStale = () => refreshId !== latestRefreshId;

            const { cart } = orderStore.currentOrderDetails;
            const appliedGiftCardIDs = orderStore.appliedGiftCardIDs;

            let currencies = null;

            if (reloadCurrencies && isDccEnabled) {
                currencies = await fetchCurrencies({
                    projectID,
                    locationID: cart.customerProjectLocationID,
                    pricedOrderCode: orderStore.pricedOrderCode,
                    appliedGiftCardIDs,
                });

                if (isStale()) {
                    return;
                }
            }

            const selection = settleCurrency({
                currencies,
                selectedCurrency: currencyStore.selectedCurrency,
                suggestedCurrency: currencyStore.suggestedCurrency,
            });
            const { currency } = selection;

            // Before the amounts - whether a deposit can be paid decides if its amount is worth asking for.
            const types = await fetchPaymentTypes({
                projectID,
                pricedOrderCode: orderStore.pricedOrderCode,
                checkoutMode: this.checkoutMode,
                appliedGiftCardIDs,
            });

            if (isStale()) {
                return;
            }

            const amountRequest = {
                projectID,
                orderCode: orderStore.pricedOrderCode,
                currency,
                appliedGiftCardIDs,
            };

            const [installments, amount, deposit, methods] = await Promise.all([
                orderStore.canBePaidInInstallments
                    ? fetchInstallments({
                        projectID,
                        installmentsOrderCode: orderStore.installmentsOrderCode,
                        exchangeRate: currency?.exchangeRateWithCommission ?? null,
                        appliedGiftCardIDs,
                    })
                    : null,
                fetchAmount({ ...amountRequest, paymentType: orderStore.orderPaymentType }),
                types?.isDepositTypeAvailable ? fetchDepositAmount(amountRequest) : null,
                fetchMethods({
                    projectID,
                    orderCode: orderStore.placedOrderCode,
                    locationIsoCode: orderStore.locationIsoCode,
                    currencyShortName: currency?.shortName ?? cart.currencyShortName,
                    reason: orderStore.paymentMethodsByOrderPaymentType,
                    appliedGiftCardIDs,
                }),
            ]);

            if (isStale()) {
                return;
            }

            currencyStore.applyCurrencySelection(selection);
            orderStore.applyPaymentTypes(types);
            this.applyInstallments(installments);
            orderStore.applyPaymentAmounts({ amount, deposit, currency });
            orderStore.applyCheckoutPaymentMethods(methods);
        },
        applyInstallments(result) {
            if (!result) {
                return;
            }

            const { data } = result;

            this.installments = data.installments;
            this.installmentsCurrencySymbol = data.currencySymbol;
            this.actionType = data.actionType;
            this.newPlanAmountConverted = data.newPlanAmountConverted;
            this.previousPlanAmountConverted = data.previousPlanAmountConverted;
            this.upgradeAmountConverted = data.upgradeAmountConverted;
            // The first instalment may be larger when the total cannot be divided evenly.
            this.installmentValue = data.installments[data.installments.length - 1].amountConverted;
        },
    },
    getters: {
        installmentsCount() {
            return this.installments?.length;
        },
        firstInstallmentAmount() {
            return this.installments[0]?.amountConverted;
        },
        firstInstallmentWithSymbol() {
            const { selectedCurrency } = useCurrencyStore();

            return selectedCurrency?.symbol
                ? Formatter.format(this.firstInstallmentAmount, 'C2', selectedCurrency.symbol)
                : Formatter.format(this.firstInstallmentAmount, 'C2');
        },
    },
});
